import { ReactElement, memo } from 'react';

import { useRovingTabIndex } from './hooks';
import { createCellEvent, getCell, getCellClassname, getCellStyle } from './utils';
import { gridTooltipId, type CellRendererProps, type PresenceMarker } from './types';
import {
  cellDraggedOverClassname,
  editableClassname,
  emptyCellClassname,
  invalidClassname,
  presenceOverlayClassname,
  requiredClassname,
} from './style/cell.css';

const PresenceMarkers = memo(function PresenceMarkers({
  markers,
}: {
  markers: readonly PresenceMarker[];
}): ReactElement {
  // One inset ring per marker, each nudged inward so stacked markers stay visible.
  const boxShadow = markers.map((marker, i) => `inset 0 0 0 ${(i + 1) * 2}px ${marker.color}`).join(', ');

  return <div className={presenceOverlayClassname} style={{ boxShadow }} />;
});

// Joined hover label for a cell's markers, or `undefined` when none carry a title.
function presenceTitle(markers: readonly PresenceMarker[]): string | undefined {
  const title = markers
    .map((marker) => marker.title)
    .filter(Boolean)
    .join(', ');
  return title || undefined;
}

function Cell<R, SR>({
  column,
  colSpan,
  isCellSelected,
  isDraggedOver,
  rangeClassname,
  copiedClassname,
  row,
  rowIdx,
  presence,
  onClick,
  onDoubleClick,
  onContextMenu,
  onCellMouseDown,
  onCellMouseEnter,
  onRowChange,
  selectCell,
  ...props
}: CellRendererProps<R, SR>): ReactElement {
  const { tabIndex, childTabIndex, onFocus } = useRovingTabIndex(isCellSelected);

  const { cellClass, cellStyle, renderCell, renderEditCell, state, dataAttrs } = getCell(column, row);
  const isEditable = renderEditCell != null && !state.locked;

  const className = getCellClassname(
    column,
    {
      [cellDraggedOverClassname]: isDraggedOver,
      [editableClassname]: isEditable,
      [requiredClassname]: isEditable && state.required,
      [emptyCellClassname]: state.empty,
      [invalidClassname]: !state.valid,
    },
    rangeClassname,
    copiedClassname,
    cellClass,
  );

  function selectCellWrapper(openEditor?: boolean): void {
    selectCell({ rowIdx, idx: column.idx }, openEditor);
  }

  function handleClick(event: React.MouseEvent<HTMLDivElement>): void {
    if (onClick) {
      const cellEvent = createCellEvent(event);
      onClick({ row, column, selectCell: selectCellWrapper }, cellEvent);
      if (cellEvent.isGridDefaultPrevented()) return;
    }
    selectCellWrapper();
  }

  function handleContextMenu(event: React.MouseEvent<HTMLDivElement>): void {
    if (onContextMenu) {
      const cellEvent = createCellEvent(event);
      onContextMenu({ row, column, selectCell: selectCellWrapper }, cellEvent);
      if (cellEvent.isGridDefaultPrevented()) return;
    }
    selectCellWrapper();
  }

  function handleDoubleClick(event: React.MouseEvent<HTMLDivElement>): void {
    if (onDoubleClick) {
      const cellEvent = createCellEvent(event);
      onDoubleClick({ row, column, selectCell: selectCellWrapper }, cellEvent);
      if (cellEvent.isGridDefaultPrevented()) return;
    }
    selectCellWrapper(true);
  }

  function handleMouseDown(event: React.MouseEvent<HTMLDivElement>): void {
    onCellMouseDown?.({ rowIdx, idx: column.idx }, event);
  }

  function handleMouseEnter(): void {
    onCellMouseEnter?.({ rowIdx, idx: column.idx });
  }

  function handleRowChange(newRow: R): void {
    onRowChange(column, newRow);
  }

  const hasPresence = presence !== undefined && presence.length > 0;
  // Anchor the presence hover label on the cell itself: the overlay is
  // `pointer-events: none`, so it can't receive hover — the cell can.
  const title = hasPresence ? presenceTitle(presence) : undefined;

  return (
    <div
      role="gridcell"
      aria-colindex={column.idx + 1} // aria-colindex is 1-based
      aria-colspan={colSpan}
      aria-selected={isCellSelected}
      aria-readonly={!isEditable || undefined}
      tabIndex={tabIndex}
      className={className}
      style={{ ...getCellStyle(column, colSpan), ...cellStyle }}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onMouseDown={onCellMouseDown ? handleMouseDown : undefined}
      onMouseEnter={onCellMouseEnter ? handleMouseEnter : undefined}
      onFocus={onFocus}
      data-testid={column.key}
      {...props}
      {...dataAttrs}
      data-tooltip-id={title ? gridTooltipId : undefined}
      data-tooltip-content={title}
      data-presence-color={title ? presence?.[0]?.color : undefined}
    >
      {renderCell?.({
        column,
        row,
        rowIdx,
        isCellEditable: isEditable,
        cellState: state,
        tabIndex: childTabIndex,
        onRowChange: handleRowChange,
      })}
      {hasPresence && <PresenceMarkers markers={presence} />}
    </div>
  );
}

export default memo(Cell) as <R, SR>(props: CellRendererProps<R, SR>) => JSX.Element;
